import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ScrollText, ShieldCheck, Cookie, Accessibility, Store, Scale, Download, Trash2, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DP_LAWS = [
  ["EU — GDPR", "General Data Protection Regulation (EU 2016/679)"],
  ["UK — UK GDPR & DPA 2018", "UK General Data Protection Regulation and Data Protection Act 2018"],
  ["Jamaica — DPA 2020", "Jamaica Data Protection Act, 2020"],
  ["USA — CCPA/CPRA", "California Consumer Privacy Act & California Privacy Rights Act"],
  ["Canada — PIPEDA", "Personal Information Protection and Electronic Documents Act"],
  ["Brazil — LGPD", "Lei Geral de Proteção de Dados"],
  ["South Africa — POPIA", "Protection of Personal Information Act"],
  ["Australia — Privacy Act 1988", "Australian Privacy Principles (APPs)"],
  ["Japan — APPI", "Act on the Protection of Personal Information"],
  ["Singapore — PDPA", "Personal Data Protection Act 2012"],
  ["India — DPDP 2023", "Digital Personal Data Protection Act, 2023"],
  ["Switzerland — nFADP", "revised Federal Act on Data Protection"],
];

const SECTIONS = [
  {
    id: "privacy", label: "Privacy", icon: ShieldCheck, title: "Privacy Policy",
    body: [
      "SHOP NOW collects only the information needed to process your orders, deliver your items and improve your experience — including your name, contact details, delivery address and order history.",
      "We never sell your personal data. Payment details are processed securely by our payment partners and are not stored on our servers.",
      "You may request a copy of your data, or ask us to delete your account, at any time by contacting our support team.",
    ],
  },
  {
    id: "terms", label: "Terms", icon: ScrollText, title: "Terms of Service",
    body: [
      "By using SHOP NOW you agree to shop, book and transact in good faith. Prices, availability and delivery estimates are shown in good faith and may change.",
      "Orders are subject to acceptance and stock availability. Where an item cannot be fulfilled, we will refund you in full.",
      "Delivery timelines are estimates. International orders are subject to customs, duties and local regulations in the destination country.",
    ],
  },
  {
    id: "cookies", label: "Cookies", icon: Cookie, title: "Cookie Policy",
    body: [
      "We use cookies and similar technologies to keep you signed in, remember your cart and preferences, and understand how the site is used so we can improve it.",
      "Essential cookies are always on. You can control non-essential cookies through your browser settings at any time.",
    ],
  },
  {
    id: "accessibility", label: "Accessibility", icon: Accessibility, title: "Accessibility Statement",
    body: [
      "SHOP NOW is committed to making our platform usable for everyone, including people who rely on assistive technologies.",
      "We aim to follow WCAG 2.1 AA guidance: keyboard navigation, sufficient colour contrast, descriptive labels and readable text.",
      "If you experience any accessibility barrier, please contact our support team and we will help and prioritise a fix.",
    ],
  },
  {
    id: "seller-terms", label: "Seller Terms", icon: Store, title: "Seller Terms",
    body: [
      "Merchants selling on SHOP NOW agree to list accurate products, honour prices shown, and fulfil orders promptly.",
      "Payouts, fees and settlement schedules are described in your merchant agreement and Merchant Dashboard.",
      "SHOP NOW may remove listings that breach our policies or applicable law.",
    ],
  },
  {
    id: "data-protection", label: "Data Protection", icon: Scale, title: "Data Protection & Your Rights",
    body: [
      "SHOP NOW observes international data protection law wherever our customers are. We apply the strictest applicable standard globally and honour your rights of access, rectification, portability, restriction, objection and erasure.",
      "You control your data. Signed-in customers can export a full copy of their data or permanently delete their account below. Guests can lodge a request and we will action it within statutory timeframes.",
      "We practise data minimisation, purpose limitation and storage limitation: we collect only what is needed, use it only for stated purposes, secure it in transit and at rest, and never sell your personal data.",
    ],
  },
];

function DataRightsPortal() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [req, setReq] = useState({ email: "", kind: "access", message: "" });

  const exportData = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/privacy/my-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "shopnow-my-data.json"; a.click();
      toast.success("Your data has been exported");
    } catch { toast.error("Could not export data"); }
    setBusy("");
  };
  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and personal data? This cannot be undone.")) return;
    setBusy("delete");
    try { await api.post("/privacy/delete-account", { confirm: true }); toast.success("Your account and data have been deleted"); setTimeout(() => { window.location.href = "/"; }, 1200); }
    catch { toast.error("Could not delete account"); setBusy(""); }
  };
  const submitReq = async (e) => {
    e.preventDefault(); setBusy("req");
    try { await api.post("/privacy/request", req); toast.success("Request received — we'll be in touch"); setReq({ email: "", kind: "access", message: "" }); }
    catch { toast.error("Could not submit request"); }
    setBusy("");
  };

  return (
    <div className="mt-6 pt-6 border-t border-border" data-testid="data-rights-portal">
      <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-emerald" /> Data Rights Portal</h3>
      <p className="text-xs text-muted-foreground mb-4">Exercise your rights under the laws below and worldwide.</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        {DP_LAWS.map(([t, d]) => (
          <div key={t} className="border border-border rounded-xl px-3 py-2" data-testid="dp-law">
            <div className="text-sm font-semibold text-ink">{t}</div><div className="text-[11px] text-muted-foreground">{d}</div>
          </div>
        ))}
      </div>
      {user ? (
        <div className="flex flex-wrap gap-3">
          <button onClick={exportData} disabled={busy === "export"} className="inline-flex items-center gap-2 bg-ink text-white rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="dp-export-btn">
            {busy === "export" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export my data
          </button>
          <button onClick={deleteAccount} disabled={busy === "delete"} className="inline-flex items-center gap-2 bg-coral text-white rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="dp-delete-btn">
            {busy === "delete" ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete my account & data
          </button>
        </div>
      ) : (
        <form onSubmit={submitReq} className="space-y-2 max-w-md" data-testid="dp-request-form">
          <input type="email" required value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })} placeholder="Your email" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="dp-req-email" />
          <select value={req.kind} onChange={(e) => setReq({ ...req, kind: e.target.value })} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="dp-req-kind">
            <option value="access">Request a copy of my data</option>
            <option value="erasure">Delete my data</option>
            <option value="rectification">Correct my data</option>
          </select>
          <textarea value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} placeholder="Anything we should know (optional)" rows={2} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="dp-req-message" />
          <button type="submit" disabled={busy === "req"} className="inline-flex items-center gap-2 bg-emerald text-white rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="dp-req-submit">
            {busy === "req" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit request
          </button>
        </form>
      )}
    </div>
  );
}

export default function Legal() {
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get("tab") || "privacy");
  React.useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && SECTIONS.some((s) => s.id === tab)) setActive(tab);
  }, [searchParams]);
  const section = SECTIONS.find((s) => s.id === active) || SECTIONS[0];
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-7 text-white mb-6">
        <div className="flex items-center gap-3 relative">
          <span className="h-12 w-12 rounded-xl bg-emerald grid place-items-center"><ScrollText size={24} /></span>
          <div>
            <h1 className="font-display text-2xl font-bold">Legal & Policies</h1>
            <p className="text-white/60 text-sm">Everything about how SHOP NOW protects you and works with our merchants.</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-6">
        <div className="flex md:flex-col gap-2 overflow-x-auto sn-scroll-x" data-testid="legal-nav">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setActive(s.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold shrink-0 transition-colors ${active === s.id ? "bg-ink text-white" : "bg-card border border-border"}`} data-testid={`legal-tab-${s.id}`}>
              <s.icon size={16} /> {s.label}
            </button>
          ))}
        </div>
        <div className="bg-card rounded-2xl border border-border p-6" data-testid={`legal-content-${section.id}`}>
          <h2 className="font-display text-xl font-bold mb-4">{section.title}</h2>
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
            {section.body.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
          {section.id === "data-protection" && <DataRightsPortal />}
          <p className="text-xs text-muted-foreground mt-6 pt-4 border-t border-border">Last updated {new Date().toLocaleDateString()}. Questions? Reach us via the Help Center.</p>
        </div>
      </div>
    </div>
  );
}
